/**
 * A read-only summary card on the service page (DNKADM-0493): a title, ONE Edit (which
 * opens the card's drawer), and label/value rows — text or chips, never a control. A card
 * with nothing to show passes `empty` and collapses to one line: its title, that sentence,
 * and the Edit. Rows are the {@link SummaryRow} below (the old Settings panel's row look).
 */
import type { ReactNode } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, resolveTokens } from '@/theme';

export interface SummaryCardProps {
  title: string;
  /** Opens the card's drawer. */
  onEdit: () => void;
  /** Hold the Edit off (with `editHint` saying why). */
  editDisabled?: boolean;
  /** When set, the card collapses to one line with this sentence. */
  empty?: string | undefined;
  /** An extra header action beside Edit (the Lengths card's "Add length"). */
  action?: ReactNode;
  /** Hide the card-level Edit (the Lengths card edits per row). */
  noEdit?: boolean;
  children?: ReactNode;
}

export function SummaryCard({
  title,
  onEdit,
  editDisabled = false,
  empty,
  action,
  noEdit = false,
  children,
}: SummaryCardProps) {
  const t = resolveTokens(useTheme());
  const edit = noEdit ? null : (
    <Button
      size="small"
      startIcon={<Icon name="edit" />}
      onClick={onEdit}
      disabled={editDisabled}
      aria-label={`Edit ${title}`}
    >
      Edit
    </Button>
  );
  const collapsed = empty !== undefined;
  return (
    <Paper
      component="section"
      aria-label={title}
      variant="outlined"
      sx={{ borderRadius: 3, borderColor: t.lineFaint, px: 2.5, py: collapsed ? 1.25 : 2 }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: collapsed ? 0 : 1 }}
      >
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1.5, minWidth: 0, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {collapsed && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {empty}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" sx={{ gap: 1, flexShrink: 0 }}>
          {action}
          {edit}
        </Stack>
      </Stack>
      {!collapsed && children}
    </Paper>
  );
}

/** One read-only label/value row. `hint` is a caption under the label; `last` drops the rule. */
export function SummaryRow({
  label,
  value,
  hint,
  last,
}: {
  label: string;
  value: ReactNode;
  hint?: string | undefined;
  last?: boolean;
}) {
  const t = resolveTokens(useTheme());
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.1,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: t.lineFaint,
      }}
    >
      <Box sx={{ flex: '0 0 40%' }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{label}</Typography>
        {hint && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
            {hint}
          </Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'right', pt: 0.25, minWidth: 0 }}>
        {typeof value === 'string' ? <Typography variant="body2">{value}</Typography> : value}
      </Box>
    </Stack>
  );
}
